import React, { createContext, useEffect, useRef } from 'react';

import useApi from '../../hooks/useApi';
import {
  extractOfferMainDetails,
  extractOfferMainDetailsV2,
  extractProgressBarMainDetails,
} from '../../utils';
import {
  EEventsType,
  EOfferType,
  EPublisherWebhookEventType,
  EPublisherWebhookEventTypeV2,
  EPublisherWebhookOfferEventType,
  EPublisherWebhookOfferEventTypeV2,
  EStorePhase,
} from '../../constants/enums';
import { OfferEventData, OfferEventDataV2 } from '../../constants/apiRequest.types';
import { OfferData, ResponseTabData } from '../../constants/apiResponses.types';
import useCustomEvents from 'hooks/useCustomEvents';
import { ProductData } from '@appcharge/shared-ui';

interface EventContextType {
  sendEvent: (event: OfferData, identifier: string) => void;
  resetObservedOffers: () => void;
}
const observedOffers = [] as string[];

export const EventBatchContext = createContext<EventContextType | undefined>(undefined);

export const EventBatchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const eventsRef = useRef<OfferData[]>([]);
  const customEvents = useCustomEvents();
  const API = useApi({});
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceDelay = 1000;

  const arrangeMixpanelData = (data: OfferData[], tabs?: ResponseTabData[]) => {
    return data.map((offer: OfferData) => ({
      offer_id: offer.offerId,
      external_id: offer.publisherOfferId,
      name: offer.offerName,
      tab: tabs?.find((tab: ResponseTabData) => tab.tabId === offer.offerTabId)?.name,
      sale_percentage: offer.productSale?.sale,
      type: offer.offerType,
      index_to_collect: offer?.indexToCollect,
      sub_type: offer?.subType,
      start_date: offer.schedule?.timeFrames[0]?.startTime,
      end_date: offer.schedule?.timeFrames[0]?.endTime,
      products: offer.productsSequence[
        offer.indexToCollect ? offer.indexToCollect - 1 : 0
      ].products.map((product: ProductData) => ({
        product_id: product.publisherProductId,
        product_name: product.name,
        product_type: product.productType,
        product_quantity: product.quantity,
      })),
      priceInUsdCents:
        offer.productsSequence[offer.indexToCollect ? offer.indexToCollect - 1 : 0]?.prices
          ?.priceInUsdCents || 0,
    }));
  };

  const sendEvent = (data: OfferData, identifier: string) => {
    if (observedOffers.includes(identifier)) return;

    observedOffers.push(identifier);
    eventsRef.current.push(data);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const tabs = API.getOffers.data?.tabs;
      const offers = eventsRef.current.filter(
        (event) => event.offerType !== EOfferType.PROGRESS_BAR
      );
      const progressBars = eventsRef.current.filter(
        (event) => event.offerType === EOfferType.PROGRESS_BAR
      );
      if (eventsRef.current.length > 0) {
        customEvents.sendCustomEvent(
          EEventsType.OFFERS_SHOWN,
          {
            offers: arrangeMixpanelData(eventsRef.current, tabs),
          },
          EStorePhase.POST_LOGIN
        );
        API.sendPublisherWebhookEvent.mutate({
          eventType: EPublisherWebhookEventType.OFFER,
          data: {
            eventName: EPublisherWebhookOfferEventType.OFFERS_SHOWN,
            offers: extractOfferMainDetails(offers, tabs),
            ...customEvents.getEventsSharedProps(),
          } as OfferEventData,
        });

        API.sendPublisherWebhookEventV2.mutate({
          eventType: EPublisherWebhookEventTypeV2.OFFERS,
          data: {
            eventName: EPublisherWebhookOfferEventTypeV2.WEBSTORE_OFFERS_DISPLAYED,
            offers: extractOfferMainDetailsV2(offers, tabs),
            progressBars: extractProgressBarMainDetails(progressBars),
            ...customEvents.getEventsSharedProps(),
          } as OfferEventDataV2,
        });
        eventsRef.current = [];
      }
    }, debounceDelay);
  };

  const resetObservedOffers = () => {
    observedOffers.length = 0;
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <EventBatchContext.Provider value={{ sendEvent, resetObservedOffers }}>
      {children}
    </EventBatchContext.Provider>
  );
};
